import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { normalisePlace, type ServiceAreaPlaceKind } from '@fieldops/shared'
import { useMemo, useState, type FormEvent } from 'react'
import { ColourPicker, nextColour } from '../../components/ColourPicker.js'
import { MapCanvas } from '../../components/map/MapCanvas.js'
import type { LngLat, ShapeCollection } from '../../components/map/surface.js'
import { Button, Card, CardHeader, EmptyState, Field, Input, SkeletonRows } from '../../components/ui.js'
import { cn } from '../../lib/cn.js'
import { errorMessage, fieldErrors, useTRPC } from '../../lib/trpc.js'

/**
 * Settings → Service areas: suburbs and postcodes grouped into the parts of
 * the map routing tries to keep together, and who covers each (ADR 021).
 *
 * The map is the ABS boundaries for the same names an address carries, so a
 * click adds exactly the string a job's suburb or postcode is matched against.
 * The search box does the same without a mouse. Nothing is written until
 * Save, like every other panel in Settings.
 */

type Kind = ServiceAreaPlaceKind

const SOURCES: Record<Kind, string> = {
  SUBURB: '/geo/wa-suburbs.json',
  POSTCODE: '/geo/wa-postcodes.json',
}

const placeKey = (kind: Kind, value: string) => `${kind}:${value}`

function parseKey(key: string): { kind: Kind; value: string } {
  const at = key.indexOf(':')
  return { kind: key.slice(0, at) as Kind, value: key.slice(at + 1) }
}

interface Boundaries {
  shapes: ShapeCollection
  /** Key to the name as the ABS writes it. */
  labels: Map<string, string>
  /** Key to its south-west and north-east corners. */
  bounds: Map<string, [LngLat, LngLat]>
}

type RawGeometry =
  | { type: 'Polygon'; coordinates: LngLat[][] }
  | { type: 'MultiPolygon'; coordinates: LngLat[][][] }

async function loadBoundaries(kind: Kind): Promise<Boundaries> {
  const response = await fetch(SOURCES[kind])
  if (!response.ok) throw new Error('The boundary file did not load.')
  const raw = (await response.json()) as {
    features: Array<{ properties: { name?: string; code?: string }; geometry: RawGeometry | null }>
  }

  const labels = new Map<string, string>()
  const bounds = new Map<string, [LngLat, LngLat]>()
  const features: ShapeCollection['features'] = []

  for (const feature of raw.features) {
    const label = feature.properties.name ?? feature.properties.code
    const value = label ? normalisePlace(kind, label) : null
    if (!value || !feature.geometry) continue
    const id = placeKey(kind, value)

    const rings =
      feature.geometry.type === 'Polygon' ? feature.geometry.coordinates : feature.geometry.coordinates.flat()
    let [west, south, east, north] = [Infinity, Infinity, -Infinity, -Infinity]
    for (const ring of rings) {
      for (const [lng, lat] of ring) {
        west = Math.min(west, lng)
        east = Math.max(east, lng)
        south = Math.min(south, lat)
        north = Math.max(north, lat)
      }
    }

    labels.set(id, label!)
    bounds.set(id, [
      [west, south],
      [east, north],
    ])
    features.push({ type: 'Feature', properties: { id }, geometry: feature.geometry })
  }

  return { shapes: { type: 'FeatureCollection', features }, labels, bounds }
}

interface Draft {
  id: string | null
  name: string
  colour: string
  /** Place keys, sorted. */
  places: string[]
  technicianIds: string[]
}

const sorted = (values: Iterable<string>) => [...values].sort()

function sameDraft(a: Draft, b: Draft): boolean {
  return (
    a.name === b.name &&
    a.colour === b.colour &&
    a.places.join('|') === b.places.join('|') &&
    sorted(a.technicianIds).join('|') === sorted(b.technicianIds).join('|')
  )
}

export function ServiceAreasPanel() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const areas = useQuery(trpc.serviceAreas.list.queryOptions())
  const people = useQuery(trpc.users.schedulable.queryOptions())

  const [mode, setMode] = useState<Kind>('SUBURB')
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const searchKind: Kind = /^\d/.test(search.trim()) ? 'POSTCODE' : 'SUBURB'
  const suburbs = useQuery({
    queryKey: ['boundaries', 'SUBURB'],
    queryFn: () => loadBoundaries('SUBURB'),
    staleTime: Infinity,
  })
  const postcodes = useQuery({
    queryKey: ['boundaries', 'POSTCODE'],
    queryFn: () => loadBoundaries('POSTCODE'),
    staleTime: Infinity,
    enabled: mode === 'POSTCODE' || searchKind === 'POSTCODE',
  })
  const boundaries = { SUBURB: suburbs.data, POSTCODE: postcodes.data }
  const shown = boundaries[mode]

  const saved = useMemo(
    () =>
      (areas.data ?? []).map((area) => ({
        ...area,
        keys: area.places.map((place) => placeKey(place.kind, place.value)),
      })),
    [areas.data],
  )
  const savedDraft = (area: (typeof saved)[number]): Draft => ({
    id: area.id,
    name: area.name,
    colour: area.colour,
    places: sorted(area.keys),
    technicianIds: area.technicianIds,
  })

  const editing = draft?.id ? saved.find((area) => area.id === draft.id) : undefined
  const dirty = draft ? (editing ? !sameDraft(draft, savedDraft(editing)) : true) : false

  /** Which saved area has each place, leaving out the one being edited. */
  const owners = useMemo(() => {
    const map = new Map<string, { name: string; colour: string }>()
    for (const area of saved) {
      if (area.id === draft?.id) continue
      for (const key of area.keys) map.set(key, { name: area.name, colour: area.colour })
    }
    return map
  }, [saved, draft?.id])

  const fills = useMemo(() => {
    const map = new Map<string, string>()
    for (const [key, owner] of owners) map.set(key, owner.colour)
    for (const key of draft?.places ?? []) map.set(key, draft!.colour)
    return map
  }, [owners, draft])

  // Framed on choosing an area, not on every click inside it.
  const frameKey = `${draft?.id ?? ''}:${mode}`
  const frame = useMemo(() => {
    const keys = editing?.keys.filter((key) => key.startsWith(`${mode}:`)) ?? []
    const corners = keys.flatMap((key) => shown?.bounds.get(key) ?? [])
    return corners.length > 0 ? corners : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameKey, shown])

  const save = useMutation(
    trpc.serviceAreas.save.mutationOptions({
      onSuccess: async (result) => {
        await Promise.all([
          queryClient.invalidateQueries(trpc.serviceAreas.pathFilter()),
          queryClient.invalidateQueries(trpc.routing.pathFilter()),
          queryClient.invalidateQueries(trpc.users.pathFilter()),
        ])
        setDraft((current) => (current ? { ...current, id: result.id } : current))
        setNotice(
          result.moved.length > 0
            ? `Saved. Moved ${result.moved
                .map((place) => `${labelOf(placeKey(place.kind, place.value))} from ${place.from}`)
                .join(', ')}.`
            : 'Saved.',
        )
      },
    }),
  )

  const remove = useMutation(
    trpc.serviceAreas.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(trpc.serviceAreas.pathFilter())
        await queryClient.invalidateQueries(trpc.routing.pathFilter())
        setDraft(null)
        setConfirmDelete(false)
        setNotice('Area deleted. Its suburbs and postcodes are free again.')
      },
    }),
  )

  function labelOf(key: string): string {
    const { kind, value } = parseKey(key)
    return boundaries[kind]?.labels.get(key) ?? value
  }

  function choose(next: Draft | null) {
    setDraft(next)
    setConfirmDelete(false)
    setNotice(null)
    save.reset()
  }

  function startNew() {
    choose({
      id: null,
      name: '',
      colour: nextColour(saved.map((area) => area.colour)),
      places: [],
      technicianIds: [],
    })
  }

  function toggle(key: string) {
    if (!draft) {
      setNotice('Choose an area on the left, or start a new one, then click the map to add to it.')
      return
    }
    setNotice(null)
    const has = draft.places.includes(key)
    setDraft({
      ...draft,
      places: has ? draft.places.filter((entry) => entry !== key) : sorted([...draft.places, key]),
    })
  }

  const matches = useMemo(() => {
    const term = search.trim()
    const source = boundaries[searchKind]
    if (term.length < 2 || !source) return []
    const wanted = normalisePlace(searchKind, term) ?? term.toUpperCase()
    const found: string[] = []
    for (const key of source.labels.keys()) {
      if (parseKey(key).value.startsWith(wanted) && !draft?.places.includes(key)) found.push(key)
      if (found.length >= 8) break
    }
    return found
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchKind, suburbs.data, postcodes.data, draft?.places])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!draft) return
    save.mutate({
      ...(draft.id ? { id: draft.id } : {}),
      name: draft.name,
      colour: draft.colour,
      places: draft.places.map(parseKey),
      technicianIds: draft.technicianIds,
    })
  }

  const errors = fieldErrors(save.error)
  const hoverOwner = hovered
    ? draft?.places.includes(hovered)
      ? draft.name || 'This area'
      : owners.get(hovered)?.name
    : undefined

  return (
    <div className="grid grid-cols-1 gap-[var(--stack-gap)] xl:grid-cols-[360px_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-[var(--stack-gap)]">
        <Card>
          <CardHeader
            title="Service areas"
            action={
              <Button size="sm" onClick={startNew}>
                New area
              </Button>
            }
          />
          {areas.isPending ? (
            <SkeletonRows rows={3} />
          ) : saved.length === 0 ? (
            <EmptyState
              title="No areas yet"
              description="Start one, then click suburbs or postcodes on the map to make it up."
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {saved.map((area) => {
                const suburbCount = area.places.filter((place) => place.kind === 'SUBURB').length
                const postcodeCount = area.places.length - suburbCount
                return (
                  <li key={area.id}>
                    <button
                      type="button"
                      aria-current={draft?.id === area.id ? 'true' : undefined}
                      onClick={() => choose(savedDraft(area))}
                      className={cn(
                        'flex min-h-[var(--control-md)] w-full items-center gap-2 px-[var(--row-pad-x)] py-[var(--row-pad-y)] text-left transition-colors',
                        draft?.id === area.id ? 'bg-ink-50' : 'hover:bg-ink-50',
                      )}
                    >
                      <span
                        aria-hidden
                        className="size-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: area.colour }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-ink-900">{area.name}</span>
                        <span className="block truncate text-xs text-ink-500">
                          {[
                            `${suburbCount} ${suburbCount === 1 ? 'suburb' : 'suburbs'}`,
                            postcodeCount ? `${postcodeCount} ${postcodeCount === 1 ? 'postcode' : 'postcodes'}` : null,
                            `${area.technicianIds.length} covering`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          <p className="border-t border-ink-200 px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
            The route planner prefers to give a job to someone who covers its area, and keeps each
            person&apos;s day in as few areas as it can. A job&apos;s area is its suburb, or its
            postcode when the suburb is in none.
          </p>
        </Card>

        {draft ? (
          <form onSubmit={submit} className="flex flex-col gap-[var(--stack-gap)]" noValidate>
            <Card>
              <CardHeader title={draft.id ? 'Edit area' : 'New area'} />
              <div className="flex flex-col gap-3 p-4">
                <div className="flex items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <Field label="Name" htmlFor="area-name" error={errors['name']} required>
                      <Input
                        id="area-name"
                        value={draft.name}
                        placeholder="North"
                        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                      />
                    </Field>
                  </div>
                  <ColourPicker
                    value={draft.colour}
                    label="Area colour"
                    onChange={(colour) => setDraft({ ...draft, colour })}
                  />
                </div>

                <Field
                  label="Add a suburb or postcode"
                  htmlFor="area-search"
                  hint="Or click it on the map."
                >
                  <Input
                    id="area-search"
                    value={search}
                    placeholder="Joondalup, or 6027"
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        if (matches[0]) {
                          toggle(matches[0])
                          setSearch('')
                        }
                      }
                    }}
                  />
                </Field>
                {matches.length > 0 ? (
                  <ul className="-mt-1 flex flex-wrap gap-1" aria-label="Matches">
                    {matches.map((key) => (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => {
                            toggle(key)
                            setSearch('')
                          }}
                          className="rounded-full bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-800 hover:bg-ink-200"
                        >
                          + {labelOf(key)}
                          {owners.get(key) ? ` (in ${owners.get(key)!.name})` : ''}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}

                <PlaceList
                  title="Suburbs"
                  keys={draft.places.filter((key) => key.startsWith('SUBURB:'))}
                  labelOf={labelOf}
                  owners={owners}
                  onRemove={toggle}
                />
                <PlaceList
                  title="Postcodes"
                  keys={draft.places.filter((key) => key.startsWith('POSTCODE:'))}
                  labelOf={labelOf}
                  owners={owners}
                  onRemove={toggle}
                />

                <fieldset>
                  <legend className="mb-1.5 text-sm font-medium text-ink-800">Who covers it</legend>
                  {people.isPending ? (
                    <SkeletonRows rows={2} />
                  ) : (
                    <ul className="grid max-h-56 grid-cols-1 gap-x-3 overflow-y-auto sm:grid-cols-2 xl:grid-cols-1">
                      {(people.data ?? []).map((person) => {
                        const checked = draft.technicianIds.includes(person.id)
                        return (
                          <li key={person.id}>
                            <label className="flex min-h-[var(--control-sm)] items-center gap-2 text-sm text-ink-800">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  setDraft({
                                    ...draft,
                                    technicianIds: checked
                                      ? draft.technicianIds.filter((id) => id !== person.id)
                                      : [...draft.technicianIds, person.id],
                                  })
                                }
                                className="size-4 rounded border-ink-300"
                              />
                              <span
                                aria-hidden
                                className="size-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: person.colour }}
                              />
                              <span className="truncate">{person.name}</span>
                            </label>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </fieldset>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ink-200 px-[var(--row-pad-x)] py-2">
                {save.error ? (
                  <p className="mr-auto text-xs text-red-600">{errorMessage(save.error)}</p>
                ) : null}
                {draft.id ? (
                  confirmDelete ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="mr-auto text-red-700"
                      loading={remove.isPending}
                      onClick={() => remove.mutate({ id: draft.id! })}
                    >
                      Delete {draft.name || 'this area'}?
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="mr-auto"
                      onClick={() => setConfirmDelete(true)}
                    >
                      Delete area
                    </Button>
                  )
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={save.isPending}
                  onClick={() => choose(editing ? savedDraft(editing) : null)}
                >
                  {editing ? 'Discard changes' : 'Cancel'}
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  variant="primary"
                  disabled={!dirty || !draft.name.trim()}
                  loading={save.isPending}
                >
                  Save area
                </Button>
              </div>
            </Card>
          </form>
        ) : null}

        {notice ? (
          <p role="status" className="text-xs text-ink-700">
            {notice}
          </p>
        ) : null}
      </div>

      <Card className="min-w-0 overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 px-[var(--row-pad-x)] py-2">
          <div
            role="group"
            aria-label="Pick by"
            className="flex items-center gap-0.5 rounded-full bg-ink-100 p-0.5"
          >
            {(['SUBURB', 'POSTCODE'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                aria-pressed={mode === kind}
                onClick={() => setMode(kind)}
                className={cn(
                  'flex min-h-[var(--control-sm)] items-center rounded-full px-3 text-xs whitespace-nowrap transition-colors',
                  mode === kind
                    ? 'bg-brand-800 font-semibold text-white'
                    : 'font-medium text-ink-600 hover:bg-white hover:text-ink-900',
                )}
              >
                {kind === 'SUBURB' ? 'Suburbs' : 'Postcodes'}
              </button>
            ))}
          </div>
          <p className="min-w-0 flex-1 truncate text-xs text-ink-500">
            {draft
              ? `Click to add to or take out of ${draft.name || 'the new area'}.`
              : 'Choose an area to edit it on the map.'}
          </p>
        </div>

        <MapCanvas
          pins={NO_PINS}
          shapes={shown?.shapes ?? null}
          fills={fills}
          frame={frame}
          onShapeClick={toggle}
          onShapeHover={setHovered}
          className="h-[70vh] min-h-[28rem]"
        >
          {hovered ? (
            <p className="pointer-events-none absolute top-2 left-2 rounded-md bg-white/95 px-2 py-1 text-xs font-medium text-ink-900 shadow-sm">
              {labelOf(hovered)}
              {hoverOwner ? <span className="font-normal text-ink-500"> · {hoverOwner}</span> : null}
            </p>
          ) : null}
          {(mode === 'SUBURB' ? suburbs : postcodes).isError ? (
            <p className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-xs text-ink-500">
              The {mode === 'SUBURB' ? 'suburb' : 'postcode'} boundaries did not load.
            </p>
          ) : null}
          <p className="pointer-events-none absolute right-2 bottom-2 rounded bg-white/85 px-1.5 py-0.5 text-[10px] text-ink-600">
            Boundaries: ABS ASGS 2021, CC BY 4.0
          </p>
        </MapCanvas>
      </Card>
    </div>
  )
}

const NO_PINS: never[] = []

function PlaceList({
  title,
  keys,
  labelOf,
  owners,
  onRemove,
}: {
  title: string
  keys: string[]
  labelOf: (key: string) => string
  owners: ReadonlyMap<string, { name: string }>
  onRemove: (key: string) => void
}) {
  if (keys.length === 0) return null
  return (
    <div>
      <p className="mb-1 text-xs font-semibold tracking-wide text-ink-500 uppercase">
        {title} · {keys.length}
      </p>
      <ul className="flex flex-wrap gap-1">
        {keys.map((key) => {
          const from = owners.get(key)?.name
          return (
            <li key={key}>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full py-0.5 pr-1 pl-2.5 text-xs',
                  from ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-200' : 'bg-ink-100 text-ink-800',
                )}
                title={from ? `Saving moves it out of ${from}` : undefined}
              >
                {labelOf(key)}
                {from ? <span className="text-amber-700">from {from}</span> : null}
                <button
                  type="button"
                  aria-label={`Remove ${labelOf(key)}`}
                  onClick={() => onRemove(key)}
                  className="grid size-5 place-items-center rounded-full text-ink-500 hover:bg-white hover:text-ink-900"
                >
                  ×
                </button>
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
